import React, { useState, useEffect } from 'react';
import NavigationBar from '../../components/NavigationBar';
import NotificationBanner from '../../components/NotificationBanner';
import PipelineColumn from './components/PipelineColumn';
import DealEditModal from './components/DealEditModal';
import AddDealButton from './components/AddDealButton';
import Icon from '../../components/AppIcon';
import { dealsService } from '../../services/crmService';

const PipelineView = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showNotification, setShowNotification] = useState(true);

  const stages = ['Lead', 'Qualified', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];

  const getDealsByStage = (stage) => {
    return deals?.filter(deal => deal?.stage === stage);
  };

  const handleDragStart = (deal) => {
    setDraggedDeal(deal);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
  };

  const handleDrop = async (targetStage) => {
    if (draggedDeal && draggedDeal?.stage !== targetStage) {
      try {
        await dealsService?.update(draggedDeal?.id, { stage: targetStage });
      } catch (err) {
        setError(err?.message || 'Failed to update deal stage');
        console.error('Error updating deal:', err);
      }
    }
    setDraggedDeal(null);
  };

  const handleEditDeal = (deal) => {
    setSelectedDeal(deal);
    setIsModalOpen(true);
  };

  const handleAddDeal = (stage) => {
    setSelectedDeal({
      id: Date.now(),
      clientName: '',
      dealValue: '',
      expectedCloseDate: '',
      stage: stage
    });
    setIsModalOpen(true);
  };

  const handleSaveDeal = async (updatedDeal) => {
    try {
      if (deals?.find(d => d?.id === updatedDeal?.id)) {
        await dealsService?.update(updatedDeal?.id, updatedDeal);
      } else {
        await dealsService?.create(updatedDeal);
      }
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to save deal');
      console.error('Error saving deal:', err);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    try {
      await dealsService?.delete(dealId);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to delete deal');
      console.error('Error deleting deal:', err);
    }
  };

  const getTotalPipelineValue = () => {
    return deals?.filter(deal => !['Closed Won', 'Closed Lost']?.includes(deal?.stage))?.reduce((sum, deal) => sum + deal?.dealValue, 0);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const handleRealtimeUpdate = ({ eventType, new: newData, old: oldData }) => {
    if (eventType === 'INSERT') {
      setDeals(prev => [...prev, newData]);
    } else if (eventType === 'UPDATE') {
      setDeals(prev => prev?.map(deal => deal?.id === newData?.id ? newData : deal));
    } else if (eventType === 'DELETE') {
      setDeals(prev => prev?.filter(deal => deal?.id !== oldData?.id));
    }
  };

  useEffect(() => {
    loadDeals();
    const unsubscribe = dealsService?.subscribeToChanges(handleRealtimeUpdate);
    return () => unsubscribe();
  }, []);

  const loadDeals = async () => {
    try {
      setLoading(true);
      const data = await dealsService?.getAll();
      setDeals(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load deals');
      console.error('Error loading deals:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20">
      <NavigationBar />
      {error && (
        <NotificationBanner
          message={error}
          type="error"
          onDismiss={() => setError('')}
        />
      )}
      {showNotification && !error && (
        <NotificationBanner
          message="Drag and drop deals between stages to update your pipeline. Click any deal card to edit details."
          type="info"
          onDismiss={() => setShowNotification(false)}
        />
      )}
      <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-2">
              Sales Pipeline
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Manage your deals through each stage of the sales process
            </p>
          </div>

          <div className="bg-card rounded-xl p-4 md:p-5 shadow-elevation-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="TrendingUp" size={20} className="text-primary" />
              </div>
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">Total Pipeline Value</p>
                <p className="text-lg md:text-xl lg:text-2xl font-bold text-foreground data-text">
                  {formatCurrency(getTotalPipelineValue())}
                </p>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading deals...</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 md:gap-5 lg:gap-6 min-w-max">
              {stages?.map(stage => (
                <div key={stage} className="flex flex-col">
                  <PipelineColumn
                    stage={stage}
                    deals={getDealsByStage(stage)}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onEditDeal={handleEditDeal}
                  />
                  <AddDealButton onClick={handleAddDeal} stage={stage} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <DealEditModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDeal(null);
        }}
        deal={selectedDeal}
        onSave={handleSaveDeal}
        onDelete={handleDeleteDeal}
      />
    </div>
  );
};

export default PipelineView;